import { isSome } from 'payments-checkout-lib/types/utils/helpers';
import { getRecurringBillingFrequency } from 'payments-checkout-lib/utils/recurring';
import { isFutureBilledCheckoutLineItem, isLineItemWithTax } from '../types/utils/typeguards';
const isBundleParent = lineItem => lineItem.bundleParent === true && isSome(lineItem.lineItemId);
const isInteractive = ({
  customConfiguration
}) => isSome(customConfiguration) && (customConfiguration.isOptional || customConfiguration.hasEditablePrice || customConfiguration.hasEditableQuantity);
export const getLineItemAmount = lineItem => lineItem.amountBeforeTax || lineItem.amount;
export const groupLineItemsByBundle = (sectionLineItems, sessionLineItems) => {
  const indexedLineItems = sectionLineItems.map((lineItem, index) => ({
    lineItem,
    index
  }));
  const membersByParentId = new Map();
  sessionLineItems.forEach(lineItem => {
    if (isBundleParent(lineItem)) {
      membersByParentId.set(lineItem.lineItemId, [lineItem]);
    }
  });
  sessionLineItems.forEach(lineItem => {
    if (!isBundleParent(lineItem) && isSome(lineItem.parentLineItemId)) {
      var _membersByParentId$ge;
      (_membersByParentId$ge = membersByParentId.get(lineItem.parentLineItemId)) === null || _membersByParentId$ge === void 0 || _membersByParentId$ge.push(lineItem);
    }
  });
  const groupedParentIds = new Set();
  membersByParentId.forEach((members, parentId) => {
    if (!members.some(isInteractive)) {
      groupedParentIds.add(parentId);
    }
  });
  const sectionParentIds = new Set();
  const childrenByParentId = new Map();
  indexedLineItems.forEach(indexedLineItem => {
    const {
      lineItem
    } = indexedLineItem;
    if (isBundleParent(lineItem)) {
      sectionParentIds.add(lineItem.lineItemId);
      return;
    }
    if (isSome(lineItem.parentLineItemId) && groupedParentIds.has(lineItem.parentLineItemId)) {
      var _childrenByParentId$g;
      childrenByParentId.set(lineItem.parentLineItemId, [...((_childrenByParentId$g = childrenByParentId.get(lineItem.parentLineItemId)) !== null && _childrenByParentId$g !== void 0 ? _childrenByParentId$g : []), indexedLineItem]);
    }
  });
  const emittedExternalParentIds = new Set();
  return indexedLineItems.flatMap(indexedLineItem => {
    var _membersByParentId$ge4, _childrenByParentId$g3;
    const {
      lineItem
    } = indexedLineItem;
    if (isBundleParent(lineItem) && groupedParentIds.has(lineItem.lineItemId)) {
      var _childrenByParentId$g2, _membersByParentId$ge2, _membersByParentId$ge3;
      const children = (_childrenByParentId$g2 = childrenByParentId.get(lineItem.lineItemId)) !== null && _childrenByParentId$g2 !== void 0 ? _childrenByParentId$g2 : [];
      const hasChildrenElsewhere = ((_membersByParentId$ge2 = (_membersByParentId$ge3 = membersByParentId.get(lineItem.lineItemId)) === null || _membersByParentId$ge3 === void 0 ? void 0 : _membersByParentId$ge3.length) !== null && _membersByParentId$ge2 !== void 0 ? _membersByParentId$ge2 : 1) > 1;
      const isFreeHeader = getLineItemAmount(lineItem) === 0;
      return children.length === 0 && hasChildrenElsewhere && isFreeHeader ? [] : [{
        type: 'bundle',
        parent: lineItem,
        parentIndex: indexedLineItem.index,
        children
      }];
    }
    const parentId = lineItem.parentLineItemId;
    if (isBundleParent(lineItem) || !isSome(parentId) || !groupedParentIds.has(parentId)) {
      return [{
        type: 'item',
        item: indexedLineItem
      }];
    }
    const externalParent = (_membersByParentId$ge4 = membersByParentId.get(parentId)) === null || _membersByParentId$ge4 === void 0 ? void 0 : _membersByParentId$ge4[0];
    if (sectionParentIds.has(parentId) || emittedExternalParentIds.has(parentId) || !externalParent) {
      return [];
    }
    emittedExternalParentIds.add(parentId);
    return [{
      type: 'bundle',
      parent: externalParent,
      parentIndex: null,
      children: (_childrenByParentId$g3 = childrenByParentId.get(parentId)) !== null && _childrenByParentId$g3 !== void 0 ? _childrenByParentId$g3 : []
    }];
  });
};
export const getBundleAmount = members => members.reduce((total, lineItem) => total + getLineItemAmount(lineItem), 0);
export const getBundleBilling = children => {
  var _billing$0$frequency, _billing$, _billing$0$numPayment, _billing$2;
  const billing = children.map(child => {
    var _child$recurringBilli, _child$recurringBilli2;
    return {
      frequency: getRecurringBillingFrequency(child.recurringBillingTerms),
      numPayments: (_child$recurringBilli = (_child$recurringBilli2 = child.recurringBillingTerms) === null || _child$recurringBilli2 === void 0 ? void 0 : _child$recurringBilli2.numPayments) !== null && _child$recurringBilli !== void 0 ? _child$recurringBilli : null
    };
  });
  const frequency = (_billing$0$frequency = (_billing$ = billing[0]) === null || _billing$ === void 0 ? void 0 : _billing$.frequency) !== null && _billing$0$frequency !== void 0 ? _billing$0$frequency : null;
  const numPayments = (_billing$0$numPayment = (_billing$2 = billing[0]) === null || _billing$2 === void 0 ? void 0 : _billing$2.numPayments) !== null && _billing$0$numPayment !== void 0 ? _billing$0$numPayment : null;
  return {
    isUniform: billing.every(child => child.frequency === frequency && child.numPayments === numPayments),
    frequency,
    numPayments
  };
};
export const getBundleDiscount = members => {
  const discountedMembers = members.filter(member => member.discount && member.discountAmount);
  const [firstDiscounted] = discountedMembers;
  if (!(firstDiscounted !== null && firstDiscounted !== void 0 && firstDiscounted.discount)) {
    return null;
  }
  const discountAmount = discountedMembers.reduce((total, member) => {
    var _member$discountAmoun;
    return total + ((_member$discountAmoun = member.discountAmount) !== null && _member$discountAmoun !== void 0 ? _member$discountAmoun : 0);
  }, 0);
  const pricedMemberCount = members.filter(member => member.unadjustedAmount > 0).length;
  const {
    valueType,
    value,
    durationType,
    numberOfPaymentsDiscounted
  } = firstDiscounted.discount;
  const hasSharedPercentage = valueType === 'PERCENT' && discountedMembers.length === pricedMemberCount && discountedMembers.every(member => {
    var _member$discount, _member$discount$numb;
    return ((_member$discount = member.discount) === null || _member$discount === void 0 ? void 0 : _member$discount.valueType) === 'PERCENT' && member.discount.value === value && member.discount.durationType === durationType && ((_member$discount$numb = member.discount.numberOfPaymentsDiscounted) !== null && _member$discount$numb !== void 0 ? _member$discount$numb : null) === (numberOfPaymentsDiscounted !== null && numberOfPaymentsDiscounted !== void 0 ? numberOfPaymentsDiscounted : null);
  });
  return {
    discountAmount,
    unadjustedAmount: members.reduce((total, member) => total + member.unadjustedAmount, 0),
    value: hasSharedPercentage ? value : discountAmount,
    valueType: hasSharedPercentage ? 'PERCENT' : 'PER_UNIT_FIXED'
  };
};
export const getBundleTax = members => {
  const taxedMembers = members.filter(isLineItemWithTax);
  if (taxedMembers.length === 0) {
    return null;
  }
  const labels = new Set(taxedMembers.map(member => member.tax.label));
  return {
    label: labels.size === 1 ? taxedMembers[0].tax.label || null : null,
    taxAmount: taxedMembers.reduce((total, member) => total + member.taxAmount, 0)
  };
};
export const getBundleFirstPayment = members => {
  const futureMembers = members.filter(isFutureBilledCheckoutLineItem);
  const [first] = futureMembers;
  if (!first) {
    return null;
  }
  const startKey = member => JSON.stringify(member.recurringBillingTerms.initialPayment);
  return {
    lineItem: first,
    isUniform: futureMembers.every(member => startKey(member) === startKey(first))
  };
};